import { Box, HStack, IconButton, Text, VStack } from '@chakra-ui/react'
import {
  ContentNodeUtils,
  DataTypeConfigUtils,
  InputValueContentNode,
  InputValueDataTypeConfig,
} from '@helo/cms'
import { Assert, List, pipe } from '@helo/utils'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useIsDark } from '../../../hooks/useIsDark'
import { Icon } from '../../Icons/Icon'
import {
  SelectInput,
  SelectInputRef,
} from '../../inputs/SelectInput/SelectInput'
import { SelectInputOption } from '../../inputs/SelectInput/SelectInput.parts'
import { BooleanFilterEditor } from '../BooleanFilterEditor/BooleanFilterEditor'
import { DateFilterEditor } from '../DateFilterEditor'
import {
  CONDITION_LABELS,
  CUBE_DATE_FILTER_CONDITION_OPTIONS,
  DATE_FILTER_CONDITION_OPTIONS,
  DATE_FILTER_CONDITION_OPTIONS_EXTENDED,
  FILTER_EDITOR_BOUNDARY_ID,
  LoadOptionsForPickListFn,
  NUMBER_CONDITION_ORDER,
  NUMBER_EXTRA_CONDITION_OPTIONS,
  STRING_CONDITION_ORDER,
  STRING_EXTRA_CONDITION_OPTIONS,
  getCubeDistinctView,
  getDistinctValueFieldKey,
  getUnmappedPath,
  getUnmappedTypeNameForField,
  isDateTimeFilterConfig,
} from '../FilterEditor/FilterEditor.parts'
import { NumberFilterEditor } from '../NumberFilterEditor/NumberFilterEditor'
import {
  ConditionOption,
  StringFilterEditor,
} from '../StringFilterEditor/StringFilterEditor'
import {
  CUBE_DATE_TIME_FILTER,
  DATE_TIME_FILTER_INPUT,
  isAnalyticsDateFilterTypeName,
} from '../dateFilterInputAdapters'
import {
  CUBE_STRING_FILTER,
  STRING_FILTER_INPUT,
  getDefaultFilterCondition,
} from '../stringFilterInputAdapters'
import {
  detectSyntheticCondition,
  mergeAndSortConditionOptions,
} from './FilterEditorRow.parts'

export interface FilterEditorRowProps {
  /** The content node for this filter row (field key + value) */
  inputValueContentNode: InputValueContentNode
  /** Array of available filter field configurations */
  filters?: InputValueDataTypeConfig[]
  /** Called when the filter row is updated (returns content node) */
  onUpdate?: (contentNode: InputValueContentNode) => void
  /** Called when the filter is removed */
  onRemove?: () => void
  /** Whether to show the remove button */
  showRemoveButton?: boolean
  /** Whether to auto-focus the input */
  autoFocus?: boolean
  /** Whether to disable the field (first) select */
  disableFieldSelect?: boolean
  /** When true, hide the field (first) select entirely (e.g. column-header filter where the field is fixed). */
  hideFieldSelect?: boolean
  /**
   * Row layout. `inline` (default) lays field/condition/value side by side.
   * `stacked` stacks condition over value vertically (e.g. the column-header filter card).
   */
  layout?: 'inline' | 'stacked'
  /** Async load options for "Is Any Of" pick list. */
  loadOptionsForPickList?: LoadOptionsForPickListFn
  /** When false, pick list dropdown should close (e.g. filter editor popover closed) */
  isFilterEditorOpen?: boolean
  /** When true (default), date filter uses extended conditions (before/after/excludes) in dropdown; when false, legacy 4 options. */
  dateConditionsInDropdown?: boolean
  /** When true, show validation error on this row */
  validationError?: boolean
  /** Click-outside boundary ID for date/range calendars (default: filter editor boundary). */
  clickBoundaryId?: string
  /** When set, date/range calendars render into this container (e.g. chip popover) so clicks stay "inside". */
  calendarContainerRef?: React.RefObject<HTMLElement | null>
  /** When set, overrides Chakra color mode for dark/light styling (e.g. from filter editor). */
  colorMode?: 'light' | 'dark'
}

export const FilterEditorRow = ({
  inputValueContentNode,
  filters = [],
  onUpdate,
  onRemove,
  showRemoveButton = true,
  autoFocus = false,
  disableFieldSelect = false,
  hideFieldSelect = false,
  layout = 'inline',
  loadOptionsForPickList,
  isFilterEditorOpen = true,
  dateConditionsInDropdown = true,
  validationError = false,
  clickBoundaryId = FILTER_EDITOR_BOUNDARY_ID,
  calendarContainerRef,
  colorMode: colorModeOverride,
  ...props
}: FilterEditorRowProps) => {
  const isDark = useIsDark(colorModeOverride)
  const fieldSelectRef = useRef<SelectInputRef>(null)
  const rowRef = useRef<HTMLDivElement>(null)

  const [selectedKey, setSelectedKey] = useState<string | undefined>(() =>
    inputValueContentNode?.key && inputValueContentNode.key !== ''
      ? inputValueContentNode.key
      : undefined,
  )

  // Focus the first (field) select when filter editor opens so user can immediately search.
  // Skip if a field is already selected — the row remounted after field selection
  // and FilterEditor will focus the condition select instead.
  useEffect(() => {
    if (autoFocus) {
      const key = inputValueContentNode?.key
      if (key && key !== '') return
      fieldSelectRef.current?.focus()
    }
  }, [autoFocus])

  // Sync selectedKey when parent passes a new content node (e.g. after field change)
  useEffect(() => {
    const key =
      inputValueContentNode?.key && inputValueContentNode.key !== ''
        ? inputValueContentNode.key
        : undefined
    setSelectedKey(key)
  }, [inputValueContentNode?.key])

  const selectOptions: SelectInputOption[] = useMemo(() => {
    const options = pipe(
      filters,
      List.map(config => ({
        label: (config.state.label as string) ?? config.key,
        value: config.key,
      })),
    )
    return [...options].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    )
  }, [filters])

  const currentSelectedOption = useMemo(() => {
    return selectOptions.find(option => option.value === selectedKey)
  }, [selectOptions, selectedKey])

  const selectedDataTypeConfig = useMemo(() => {
    return filters.find(config => config.key === selectedKey)
  }, [filters, selectedKey])

  /** Field config from this row's content node (prop). Use for loadOptions so we always query for the correct field when the chip editor opens, instead of relying on state that may be stale. */
  const rowFieldConfigForLoadOptions = useMemo(() => {
    const key = inputValueContentNode?.key
    if (!key || key === '') return undefined
    return filters.find(config => config.key === key)
  }, [filters, inputValueContentNode?.key])

  // Schema config for condition options / type routing. Live values stay on ContentNode.data.
  // When no field is selected, use first available filter as placeholder so condition + value inputs still render.
  const currentConfig = useMemo(() => {
    if (selectedKey && selectedKey !== '') {
      const baseConfig =
        selectedDataTypeConfig ?? inputValueContentNode.getDataTypeConfig()
      return {
        ...baseConfig,
        key: selectedKey,
      }
    }
    if (filters.length > 0) {
      const placeholder = filters[0]
      return {
        ...placeholder,
        key: '',
        ofType: placeholder.ofType
          ? {
              ...placeholder.ofType,
              state: {
                ...(placeholder.ofType.state ?? {}),
                initialValue: undefined,
              },
            }
          : placeholder.ofType,
      }
    }
    return null
  }, [selectedKey, selectedDataTypeConfig, inputValueContentNode, filters])
  const filterTypeName = currentConfig?.ofType?.typeName
  const isDateTimeFilter = filterTypeName === DATE_TIME_FILTER_INPUT

  // Get condition options: use Date/Range/Blank/Not Blank for date; extended (with before/after/excludes) when dateConditionsInDropdown
  const conditionOptions: ConditionOption[] = useMemo(() => {
    if (isDateTimeFilter) {
      return dateConditionsInDropdown
        ? DATE_FILTER_CONDITION_OPTIONS_EXTENDED
        : DATE_FILTER_CONDITION_OPTIONS
    }
    if (isAnalyticsDateFilterTypeName(filterTypeName)) {
      return CUBE_DATE_FILTER_CONDITION_OPTIONS
    }
    if (!currentConfig?.ofType) return []

    const obj = pipe(
      currentConfig,
      DataTypeConfigUtils.getOfType(),
      Assert.orReturn(DataTypeConfigUtils.isKind('INPUT_OBJECT'), () => null),
    )

    if (!obj) return []

    try {
      const fields = DataTypeConfigUtils.getFields()(obj)
      const schemaOptions = fields
        .filter(field => field.key !== 'not' && field.key !== 'mode')
        .map(field => ({
          label:
            CONDITION_LABELS[field.key] ??
            (field.state.label as string) ??
            field.key,
          value: field.key,
          isArray: field.ofType?.kind === 'LIST',
        }))

      const allowedStringKeys = new Set(STRING_CONDITION_ORDER)

      switch (filterTypeName) {
        case STRING_FILTER_INPUT:
        case CUBE_STRING_FILTER:
          return mergeAndSortConditionOptions(
            schemaOptions,
            STRING_EXTRA_CONDITION_OPTIONS,
            STRING_CONDITION_ORDER,
            o => allowedStringKeys.has(o.value),
          )
        case 'IntFilterInput':
        case 'FloatFilterInput':
          return mergeAndSortConditionOptions(
            schemaOptions,
            NUMBER_EXTRA_CONDITION_OPTIONS,
            NUMBER_CONDITION_ORDER,
            o => o.value !== 'in' && o.value !== 'notIn',
          )
        case 'BooleanFilterInput':
          return [{ label: 'Equals', value: 'equals' }]
        default:
          return mergeAndSortConditionOptions(
            schemaOptions,
            STRING_EXTRA_CONDITION_OPTIONS,
            STRING_CONDITION_ORDER,
            o => allowedStringKeys.has(o.value),
          )
      }
    } catch {
      return []
    }
  }, [
    currentConfig,
    isDateTimeFilter,
    filterTypeName,
    dateConditionsInDropdown,
  ])

  // Find initial condition from currentValue or ofType.state.initialValue
  const currentValue = inputValueContentNode?.data as
    | Record<string, any>
    | undefined
  const ofTypeInitialValue = currentConfig?.ofType?.state?.initialValue as
    | Record<string, any>
    | undefined

  const initialSelectedField = useMemo(() => {
    if (currentValue) {
      const synthetic = detectSyntheticCondition(
        currentValue,
        filterTypeName,
        dateConditionsInDropdown,
      )
      if (synthetic)
        return conditionOptions.find(opt => opt.value === synthetic)

      for (const option of conditionOptions) {
        if (currentValue[option.value] !== undefined) return option
        if (
          isAnalyticsDateFilterTypeName(filterTypeName) &&
          option.value === 'inRange' &&
          currentValue.inDateRange !== undefined
        ) {
          return option
        }
      }
    }

    if (ofTypeInitialValue && typeof ofTypeInitialValue === 'object') {
      for (const option of conditionOptions) {
        if (ofTypeInitialValue[option.value] !== undefined) return option
      }
    }

    if (selectedKey && selectedKey !== '' && conditionOptions.length > 0) {
      const defaultCondition = getDefaultFilterCondition(filterTypeName)
      return conditionOptions.find(opt => opt.value === defaultCondition)
    }

    return undefined
  }, [
    currentValue,
    ofTypeInitialValue,
    conditionOptions,
    isDateTimeFilter,
    filterTypeName,
    dateConditionsInDropdown,
    selectedKey,
  ])

  const resolvedInitialSelectedField = useMemo(() => {
    if (initialSelectedField) return initialSelectedField
    if (!selectedKey || selectedKey === '' || conditionOptions.length === 0) {
      return undefined
    }
    const defaultCondition = getDefaultFilterCondition(filterTypeName)
    return conditionOptions.find(opt => opt.value === defaultCondition)
  }, [initialSelectedField, selectedKey, conditionOptions, filterTypeName])

  const [selectedField, setSelectedField] = useState<
    ConditionOption | undefined
  >(resolvedInitialSelectedField)

  // Use a ref to track the current selectedField to avoid closure issues
  const selectedFieldRef = useRef<ConditionOption | undefined>(
    resolvedInitialSelectedField,
  )

  const updateSelectedField = (field: ConditionOption | undefined) => {
    setSelectedField(field)
    selectedFieldRef.current = field
  }

  // Sync selectedField from filter value when it matches stored data; do not clear when initialSelectedField is undefined
  // (user may have just selected a condition and we haven't written the value yet, which would cause a flicker)
  useEffect(() => {
    if (resolvedInitialSelectedField) {
      if (
        !selectedField ||
        selectedField.value === resolvedInitialSelectedField.value
      ) {
        updateSelectedField(resolvedInitialSelectedField)
      }
    }
  }, [resolvedInitialSelectedField, selectedField])

  // Form for filter value input (used by StringFilterEditor and NumberFilterEditor)
  type FilterEditorFormFields = {
    filterValue: string
  }

  const { register, handleSubmit, setValue, watch } =
    useForm<FilterEditorFormFields>({
      defaultValues: { filterValue: '' },
    })

  // Content node for child editors (including placeholder when no field selected)
  const currentContentNode = useMemo(() => {
    if (!currentConfig) return null
    if (selectedKey && selectedKey !== '') {
      return inputValueContentNode
    }
    return ContentNodeUtils.createContentNodeWithSelection(
      currentConfig,
      inputValueContentNode?.data ?? {},
    ) as InputValueContentNode
  }, [currentConfig, selectedKey, inputValueContentNode])

  const isFieldSelected = !!(selectedKey && selectedKey !== '')
  const isStacked = layout === 'stacked'

  const renderFilterEditor = () => {
    if (!currentConfig || !currentContentNode) return null

    const commonProps = {
      autoFocus: autoFocus,
      flex: '1' as const,
      minWidth: 0,
      isDisabled: !isFieldSelected,
      stopEnterPropagationWhenMenuOpen: true,
      onConditionSelected: () => {
        requestAnimationFrame(() => {
          const pickListInput = rowRef.current?.querySelector(
            '[data-testid="filter-picklist-search-input"]',
          ) as HTMLElement | null
          if (pickListInput) {
            pickListInput.focus()
            return
          }
          const valueInput = rowRef.current?.querySelector(
            '[data-testid="string-filter-value-input"], [data-testid="number-filter-value-input"], [data-testid="string-filter-inlist-input"]',
          ) as HTMLElement | null
          if (valueInput) {
            valueInput.focus()
            return
          }
          const dateRangeFrom = rowRef.current?.querySelector(
            '[data-testid="date-range-from-input"]',
          ) as HTMLElement | null
          if (dateRangeFrom) {
            dateRangeFrom.focus()
            return
          }
          const inputs = rowRef.current?.querySelectorAll('input')
          if (inputs?.[2]) (inputs[2] as HTMLElement).focus()
        })
      },
    }

    const formMethods = {
      register,
      handleSubmit,
      setValue,
      watch,
    }

    switch (filterTypeName) {
      case STRING_FILTER_INPUT:
      case CUBE_STRING_FILTER:
        return (
          <StringFilterEditor
            {...commonProps}
            inputValueContentNode={currentContentNode}
            conditionOptions={conditionOptions}
            selectedField={selectedField}
            updateSelectedField={updateSelectedField}
            selectedFieldRef={selectedFieldRef}
            formMethods={formMethods}
            onUpdate={handleChildUpdate}
            loadOptionsForPickList={
              loadOptionsForPickList
                ? (search, limit, notIn, skip) => {
                    const distinctTypeOverride =
                      typeof rowFieldConfigForLoadOptions?.state
                        ?.filterDistinctInputType === 'string' &&
                      rowFieldConfigForLoadOptions.state
                        .filterDistinctInputType !== ''
                    const cubeDistinctView = getCubeDistinctView(
                      rowFieldConfigForLoadOptions,
                    )
                    return loadOptionsForPickList({
                      unmappedTypeName: getUnmappedTypeNameForField(
                        rowFieldConfigForLoadOptions,
                      ),
                      unmappedFieldKey:
                        getDistinctValueFieldKey(
                          rowFieldConfigForLoadOptions,
                        ) ??
                        inputValueContentNode?.key ??
                        '',
                      search,
                      limit,
                      notIn,
                      skip,
                      currentFieldKey: inputValueContentNode?.key,
                      cubeDistinctView,
                      // Cube field paths are not valid for Prisma distinct;
                      // omit when using an explicit filterDistinctInputType
                      // or cube-native distinct.
                      unmappedPath:
                        distinctTypeOverride || cubeDistinctView
                          ? undefined
                          : getUnmappedPath(rowFieldConfigForLoadOptions),
                    })
                  }
                : undefined
            }
            isFilterEditorOpen={isFilterEditorOpen}
            clickBoundaryId={clickBoundaryId}
            colorMode={colorModeOverride}
          />
        )
      case DATE_TIME_FILTER_INPUT:
      case CUBE_DATE_TIME_FILTER:
        return (
          <DateFilterEditor
            {...commonProps}
            inputValueContentNode={currentContentNode}
            conditionOptions={conditionOptions}
            selectedField={selectedField}
            updateSelectedField={updateSelectedField}
            selectedFieldRef={selectedFieldRef}
            formMethods={formMethods}
            onUpdate={handleChildUpdate}
            dateConditionsInDropdown={dateConditionsInDropdown}
            clickBoundaryId={clickBoundaryId}
            calendarContainerRef={calendarContainerRef}
            isFilterEditorOpen={isFilterEditorOpen}
          />
        )
      case 'IntFilterInput':
      case 'FloatFilterInput':
        return (
          <NumberFilterEditor
            {...commonProps}
            inputValueContentNode={currentContentNode}
            conditionOptions={conditionOptions}
            selectedField={selectedField}
            updateSelectedField={updateSelectedField}
            selectedFieldRef={selectedFieldRef}
            formMethods={formMethods}
            onUpdate={handleChildUpdate}
            clickBoundaryId={clickBoundaryId}
            isFilterEditorOpen={isFilterEditorOpen}
          />
        )
      case 'BooleanFilterInput':
        return (
          <BooleanFilterEditor
            {...commonProps}
            inputValueContentNode={currentContentNode}
            conditionOptions={conditionOptions}
            selectedField={selectedField}
            updateSelectedField={updateSelectedField}
            selectedFieldRef={selectedFieldRef}
            onUpdate={handleChildUpdate}
            isFilterEditorOpen={isFilterEditorOpen}
          />
        )
      default:
        return (
          <StringFilterEditor
            {...commonProps}
            inputValueContentNode={currentContentNode}
            conditionOptions={conditionOptions}
            selectedField={selectedField}
            updateSelectedField={updateSelectedField}
            selectedFieldRef={selectedFieldRef}
            formMethods={formMethods}
            onUpdate={handleChildUpdate}
            clickBoundaryId={clickBoundaryId}
            colorMode={colorModeOverride}
            isFilterEditorOpen={isFilterEditorOpen}
          />
        )
    }
  }

  const handleFieldChange = (option: SelectInputOption | null) => {
    if (!option || !onUpdate) return

    const selectedConfig = filters.find(config => config.key === option.value)

    if (selectedConfig) {
      setSelectedKey(option.value)
      const previousConfig = filters.find(
        config => config.key === inputValueContentNode?.key,
      )
      const wasDateType = isDateTimeFilterConfig(previousConfig)
      const isDateType = isDateTimeFilterConfig(selectedConfig)
      // When changing between date and non-date (either direction), do not persist the previous value
      const shouldClearValue = wasDateType !== isDateType
      const existingData = shouldClearValue
        ? {}
        : inputValueContentNode?.data !== undefined &&
            inputValueContentNode?.data !== null &&
            typeof inputValueContentNode.data === 'object'
          ? (inputValueContentNode.data as Record<string, unknown>)
          : {}
      const newContentNode = ContentNodeUtils.createContentNodeWithSelection(
        selectedConfig,
        existingData,
      ) as InputValueContentNode
      onUpdate(newContentNode)
    }
  }

  // Wrap child onUpdate: preserve the field key from the current row so
  // condition-only updates don't clear the selected field.
  const handleChildUpdate = (updatedContentNode: InputValueContentNode) => {
    if (!onUpdate) return
    const currentKey =
      (inputValueContentNode?.key &&
        inputValueContentNode.key !== '' &&
        inputValueContentNode.key) ||
      selectedKey
    if (
      currentKey &&
      (!updatedContentNode.key || updatedContentNode.key === '')
    ) {
      const configWithKey = {
        ...updatedContentNode.getDataTypeConfig(),
        key: currentKey,
      }
      onUpdate(
        ContentNodeUtils.createContentNodeWithSelection(
          configWithKey,
          updatedContentNode.data ?? {},
        ) as InputValueContentNode,
      )
      return
    }
    onUpdate(updatedContentNode)
  }

  return (
    <Box
      width="100%"
      data-part="FilterEditorRow"
      data-testid="filter-editor-row"
      {...props}
    >
      {isStacked ? (
        <VStack
          ref={rowRef}
          gap="8px"
          align="stretch"
          width="100%"
          minWidth={0}
        >
          {filters.length > 0 && !hideFieldSelect && (
            <Box data-testid="filter-field-select" flex="1" minWidth={0}>
              <SelectInput
                ref={fieldSelectRef}
                options={selectOptions}
                value={currentSelectedOption}
                onChange={handleFieldChange}
                filterMode="substring"
                sortByMatch
                placeholder="Search or select filter"
                flex="1"
                minWidth={0}
                isSearchable
                isDisabled={disableFieldSelect}
                menuIsOpen={isFilterEditorOpen ? undefined : false}
              />
            </Box>
          )}
          {renderFilterEditor()}
          {showRemoveButton && onRemove && (
            <IconButton
              aria-label="Remove filter"
              padding="8px"
              size="sm"
              flexShrink={0}
              icon={
                <Icon
                  icon="XIcon"
                  boxSize="12px"
                  color={isDark ? 'presets.neutral.0' : 'presets.neutral.90'}
                />
              }
              onClick={onRemove}
            />
          )}
        </VStack>
      ) : (
        <HStack
          ref={rowRef}
          gap="8px"
          alignItems={
            selectedField?.value === 'inRange' ? 'flex-end' : 'flex-start'
          }
          width="100%"
          flexWrap="nowrap"
          minWidth={0}
        >
          {filters.length > 0 && !hideFieldSelect && (
            <Box data-testid="filter-field-select" flex="1" minWidth={0}>
              <SelectInput
                ref={fieldSelectRef}
                options={selectOptions}
                value={currentSelectedOption}
                onChange={handleFieldChange}
                filterMode="substring"
                sortByMatch
                placeholder="Search or select filter"
                flex="1"
                minWidth={0}
                isSearchable
                isDisabled={disableFieldSelect}
                menuIsOpen={isFilterEditorOpen ? undefined : false}
              />
            </Box>
          )}
          {renderFilterEditor()}
          {showRemoveButton && onRemove && (
            <IconButton
              aria-label="Remove filter"
              padding="8px"
              size="sm"
              flexShrink={0}
              icon={
                <Icon
                  icon="XIcon"
                  boxSize="12px"
                  color={isDark ? 'presets.neutral.0' : 'presets.neutral.90'}
                />
              }
              onClick={onRemove}
            />
          )}
        </HStack>
      )}
      {validationError && (
        <Text
          fontSize="11px"
          color={isDark ? 'presets.red.40' : 'presets.red.60'}
          mt="4px"
          ml="4px"
        >
          Please complete this filter or remove it
        </Text>
      )}
    </Box>
  )
}

FilterEditorRow.displayName = 'FilterEditorRow'
